import * as React from 'react';
import {getReactButton} from "../../index";
import {ReactButtonState} from "./ReactButton";
import {RefObject} from "react";

interface ReactAccordionData {
    id?: string,
    text?: {},
    content?: {},
    style?: {},
    css?: string,
    expandedCSS?: string,
    expandedStyle?: {},
    contentStyle?: {},
    contentCSS?: string,
    dataKey: string,
    row: any,
    rowIndex: number,
    onClick: (rowObj: any, shiftKey: boolean, ctrlKey: boolean, rowIndex: number) => boolean,
    tabIndex?: null,
    refreshID?: string,
    onRefresh?: () => {},
    title?: string,
    expandedID?: string,
    stopPropagationOnClick?: boolean,
    disabled?: boolean,
    disableID?: string,
    arrowExpandCSS?: string,
    arrowCollapseCSS?: string,
    arrowStyle?: {},
    enableDragAndDrop?: boolean,
    shellID?: string,
    onDragStart?: (event: React.DragEvent<HTMLTableRowElement>, row: any) => void,
    onDragStop?: (event: React.DragEvent<HTMLTableRowElement>) => void,
    onDuringDrag?: (event: React.DragEvent<HTMLTableRowElement>, row: any) => void,
    collapsedCSS?: string,
    collapsedStyle?: {},
    allowExpandAndCollapse?: boolean,
    onWheel?: (event: any, row: any) => boolean,
    leftOfArrowContent?: (row: any, rowIndex: number, disabled: boolean, doHighlight: boolean) => any,
    leftOfArrowJSONHierarchyContent?: any,
    doHighlight?: boolean,
    headerDivCSS?: string,
    onCollapseExpandAccordion?: (rowObj: any, rowIndex: number, shiftKey: boolean, ctrlKey: boolean) => boolean,
    selectRecordsWithRowDrag?: boolean,
    onHeaderClick?: (rowObj: any, rowIndex: number) => void,  // Called when header is clicked (for focus tracking)
}

interface ReactAccordionState {
    row: any,
    disabled: boolean,
    content: {},
}

export class ReactAccordion extends React.Component<ReactAccordionData> {
    allowExpandAndCollapse: boolean;
    contentDivRef: RefObject<any>;
    h3Ref: RefObject<HTMLHeadingElement>;

    constructor(props: ReactAccordionData) {
        super(props);

        this.contentDivRef = React.createRef();
        this.h3Ref = React.createRef();

        this.state = {
            content: props.content,
            disabled: (props.disabled),
            row: props.row,
        };

        this.allowExpandAndCollapse = (null != props.allowExpandAndCollapse) ? props.allowExpandAndCollapse : true;

        // Binding is necessary to allow these functions access to the State data
        this.hideShowAccordion = this.hideShowAccordion.bind(this);
        this.onRefresh = this.onRefresh.bind(this);
        this.onExpandedClick = this.onExpandedClick.bind(this);
        this.onDisableClick = this.onDisableClick.bind(this);
        this.startDragRow = this.startDragRow.bind(this);
        this.stopDragRow = this.stopDragRow.bind(this);
        this.duringDragRow = this.duringDragRow.bind(this);
        this.onAccordionScroll = this.onAccordionScroll.bind(this);
    }

    // Update state from props without using deprecated lifecycle
    static getDerivedStateFromProps(props: ReactAccordionData, state: ReactAccordionState): Partial<ReactAccordionState> | null {
        return {
            content: props.content,
            disabled: props.disabled,
            row: props.row,
        };
    }

    componentDidMount() {
        if (this.contentDivRef.current) {
            this.contentDivRef.current.addEventListener('wheel', this.onAccordionScroll, { passive: false });
        }
    }

    componentDidUpdate(prevProps: Readonly<ReactAccordionData>) {
        if (this.h3Ref.current && this.h3Ref.current.id && document.activeElement && (document.activeElement as HTMLElement).id === this.h3Ref.current.id) {
            this.h3Ref.current.focus();
        }
    }

    componentWillUnmount() {
        if (this.contentDivRef.current) {
            this.contentDivRef.current.removeEventListener('wheel', this.onAccordionScroll, { passive: false });
        }
    }

    onAccordionScroll(event: any) {
        if(null != this.props.onWheel) {
            const wheelUp = event.deltaY < 0;
            if(this.props.onWheel(this.props.row, wheelUp)) {
                event.stopPropagation(); // Your Scroll is MINE
                event.preventDefault();
            }
        }
    }

    getAccordionIcon() {
        if(!this.allowExpandAndCollapse || (Object.keys(this.props.content).length <= 0)) {
            return "";
        } else if(null != (this.state as ReactAccordionState).row[this.props.dataKey] && (this.state as ReactAccordionState).row[this.props.dataKey]) {
            return this.props.arrowExpandCSS;
        }
        else {
            return this.props.arrowCollapseCSS;
        }
    }

    hideShowAccordion(event: React.MouseEvent<HTMLInputElement, MouseEvent>) {
        // Check if the click came from a text entry element - if so, don't focus the header or expand/collapse
        const target = event.target as HTMLElement;
        if (target) {
            const tagName = target.tagName;
            // Block if clicking on textarea, select, or text inputs
            if (tagName === 'TEXTAREA' || tagName === 'SELECT') {
                return;
            }
            if (tagName === 'INPUT') {
                const input = target as HTMLInputElement;
                const inputType = (input.type || '').toLowerCase();
                if (inputType !== 'radio' && inputType !== 'checkbox') {
                    return;
                }
            }
        }

        if (this.h3Ref.current) {
            this.h3Ref.current.focus();
        }
        // Notify focus tracking (always, regardless of expand/collapse permission)
        if (null != this.props.onHeaderClick) {
            this.props.onHeaderClick((this.state as ReactAccordionState).row, this.props.rowIndex);
        }

        let allowExpandAndCollapse = this.allowExpandAndCollapse;
        if(null != this.props.onCollapseExpandAccordion) {
            allowExpandAndCollapse = this.props.onCollapseExpandAccordion((this.state as ReactAccordionState).row, this.props.rowIndex, event.shiftKey, event.ctrlKey);
        }
        if(allowExpandAndCollapse) {
            const isExpanded = this.props.onClick((this.state as ReactAccordionState).row, event.shiftKey, event.ctrlKey, this.props.rowIndex);

            (this.state as ReactAccordionState).row[this.props.dataKey] = isExpanded;

            this.setState({
                row: (this.state as ReactAccordionState).row,
            });
        }
    }

    onRefresh() {
        if(null != this.props.onRefresh) {
            this.props.onRefresh();
        }

        this.setState(() => ({
            row: (this.state as ReactAccordionState).row,
        }));
    }

    onExpandedClick(event: any) {
        if(this.allowExpandAndCollapse) {
            const isExpanded = $(event.currentTarget).is(":checked");

            (this.state as ReactAccordionState).row[this.props.dataKey] = isExpanded;

            this.setState({
                row: (this.state as ReactAccordionState).row
            });
        }
    }

    onDisableClick(event: any) {
        const isChecked = $(event.currentTarget).is(":checked");
        this.setState({
            disabled: isChecked
        });
    }

    startDragRow(event: React.DragEvent<HTMLTableRowElement>) {
        if (!(this.state as ReactAccordionState).disabled && (this.props.enableDragAndDrop || this.props.selectRecordsWithRowDrag) && null != (this.state as ReactAccordionState).row) {
            this.props.onDragStart(event, (this.state as ReactAccordionState).row);
        }

        // Prevents the drag row animation, since we are not actually moving the row anywhere
        if(this.props.selectRecordsWithRowDrag) {
            event.dataTransfer.setDragImage(new Image(), 0, 0);
        }

        event.stopPropagation();
    }

    stopDragRow(event: React.DragEvent<HTMLTableRowElement>) {
        if (!(this.state as ReactAccordionState).disabled && (this.props.enableDragAndDrop || this.props.selectRecordsWithRowDrag)) {
            this.props.onDragStop(event);
        }
    }

    duringDragRow(event: React.DragEvent<HTMLTableRowElement>) {
        if (!(this.state as ReactAccordionState).disabled && (this.props.enableDragAndDrop || this.props.selectRecordsWithRowDrag)) {
            this.props.onDuringDrag(event, (this.state as ReactAccordionState).row);
        }

        event.stopPropagation();
    }

    getLeftOfArrowContent() {
        if(null != this.props.leftOfArrowJSONHierarchyContent) {
            return this.props.leftOfArrowJSONHierarchyContent;
        } else if(null != this.props.leftOfArrowContent) {
            return this.props.leftOfArrowContent(this.props.row, this.props.rowIndex, this.props.disabled, this.props.doHighlight);
        } else {
            return null;
        }
    }

    getAccordion(isExpanded: boolean) {
        const h3BaseStyle = (isExpanded) ? this.props.expandedStyle : this.props.collapsedStyle;
        const h3Style = Object.assign({}, h3BaseStyle, { outline: 'none' });
        if(this.props.title) {
            return <h3
                ref={this.h3Ref}
                id={this.props.id}
                tabIndex={this.props.tabIndex}
                className={(isExpanded) ? this.props.expandedCSS : this.props.collapsedCSS}
                style={h3Style}
                title={this.props.title}
                onClick={this.hideShowAccordion}
                onFocus={() => {
                    if (null != this.props.onHeaderClick) {
                        this.props.onHeaderClick((this.state as ReactAccordionState).row, this.props.rowIndex);
                    }
                }}
            >
                {this.getLeftOfArrowContent()}
                <div className={this.props.headerDivCSS}>
                    <span data-isExpanded={isExpanded} data-hasContent={(Object.keys(this.props.content).length > 0)} id={this.props.id + '_arrow'} className={this.getAccordionIcon()} style={this.props.arrowStyle}></span>
                    {this.props.text}
                </div>
            </h3>
        } else {
            return <h3
                ref={this.h3Ref}
                id={this.props.id}
                tabIndex={this.props.tabIndex}
                className={(isExpanded) ? this.props.expandedCSS : this.props.collapsedCSS}
                style={h3Style}
                onClick={this.hideShowAccordion}
                onFocus={() => {
                    if (null != this.props.onHeaderClick) {
                        this.props.onHeaderClick((this.state as ReactAccordionState).row, this.props.rowIndex);
                    }
                }}
            >
                {this.getLeftOfArrowContent()}
                <div className={this.props.headerDivCSS}>
                    <span data-isExpanded={isExpanded} data-hasContent={(Object.keys(this.props.content).length > 0)} id={this.props.id + '_arrow'} className={this.getAccordionIcon()} style={this.props.arrowStyle}></span>
                    {this.props.text}
                </div>
            </h3>
        }
    }

    render() {
        const isExpanded = (this.allowExpandAndCollapse) ? null != (this.state as ReactAccordionState).row[this.props.dataKey] && (this.state as ReactAccordionState).row[this.props.dataKey] : true;
        let accordionCSS = "";
        if(this.props.css.length > 0) {
            if((!(this.state as ReactAccordionState).disabled) && this.props.enableDragAndDrop) {
                accordionCSS = this.props.css + ' ui-draggable';
            } else {
                accordionCSS = this.props.css;
            }
        } else {
            if((!(this.state as ReactAccordionState).disabled) && this.props.enableDragAndDrop) {
                accordionCSS = 'ui-draggable';
            }
        }

        return <div id={this.props.shellID} className={accordionCSS} style={this.props.style} draggable={(((this.state as ReactAccordionState).disabled) ? false : (this.props.enableDragAndDrop || this.props.selectRecordsWithRowDrag))} onDragStart={this.startDragRow} onDragEnd={this.stopDragRow} onDragEnter={this.duringDragRow}>
            {getReactButton({id: this.props.refreshID, style: {border: "0px", padding: "0px"}, css: "categoryAllHide", onClick: this.onRefresh, text: "HIDDEN REFRESH BUTTON"})}
            {<table className="categoryAllHide"><tr>
                <td><input
                    id={this.props.expandedID}
                    type="checkbox"
                    onChange={this.onExpandedClick}
                    checked={isExpanded}
                /></td>
                <td>
                    HIDDEN EXPANDED CHECKBOX
                </td>
            </tr><tr>
                <td><input
                    id={this.props.disableID}
                    type="checkbox"
                    onChange={this.onDisableClick}
                    checked={(this.state as ReactAccordionState).disabled}
                /></td>
                <td>
                    HIDDEN DISABLE BUTTON
                </td>
            </tr></table>
            }
            {this.getAccordion(isExpanded)}
            <div id={this.props.id + '_expandDiv'} className = {((isExpanded) ? 'fadeIn' : 'fadeOut')}>
                <div ref={this.contentDivRef} id={this.props.id + '_contentDiv'} className={this.props.contentCSS} style={this.props.contentStyle}>
                    {(isExpanded) ? (this.state as ReactAccordionState).content : null}
                </div>
            </div>
        </div>;
    }
}
